/**
 * A function to create a log object for the 'homePageOpened' event (evid: 200)
 *
 * Event description: triggered when blog home page opened
 *
 * @param {homePageOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<homePageOpenedParams>} LogObject<homePageOpenedParams>
 */
export function homePageOpened(params) {
    return { evid: 200, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'performanceClientSide' event (evid: 2003)
 *
 * Event description: Tracking performance when a user opens the blog
 *
 * @param {performanceClientSideParams} params - The parameters for this event
 * @property {GUID} instance_id - instance id
 * @property {number} initial_connection_time - from browser, in ms
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} biToken - msid
 * @property {string} context - where it came from
 * @property {GUID} visitor_id - visitor_id
 * @property {number} content_transfer_time - only html, in ms
 * @property {number} dom_content_load - images, in ms
 * @property {number} dom_interactive - CSS and JS download + parsing + execution, in ms
 * @property {GUID} post_stable_id - post_stable_id
 * @property {number} content_transfer_size - only html in KB
 * @return {LogObject<performanceClientSideParams>} LogObject<performanceClientSideParams>
 */
export function performanceClientSide(params) {
    return { evid: 2003, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'categoryPageOpened' event (evid: 201)
 *
 * Event description: triggered when blog category page opened
 *
 * @param {categoryPageOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_name - category name
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {string} category_id - category_id
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<categoryPageOpenedParams>} LogObject<categoryPageOpenedParams>
 */
export function categoryPageOpened(params) {
    return { evid: 201, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postPageOpened' event (evid: 202)
 *
 * Event description: triggered when post page opened
 *
 * @param {postPageOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {number} paid_plan_has_access - 1 - has access, 0 - don't have access, null - not paid post
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - new immutable post id
 * @property {string} language - post language (multilingual)
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} original_instance_id - in the members app events. holds the child app (forum/blog) instance_id
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - new id (contact_id in members enriched table?)
 * @property {string} paid_plan_id - list (array) of paid plan ids
 * @property {Url} referrer - URL from where did the user come
 * @property {string} member_id - member_id - Old not used anymore
 * @property {string} reading_session_id - user reading session id
 * @property {string} referral_post_id - the post_id that got the user here
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<postPageOpenedParams>} LogObject<postPageOpenedParams>
 */
export function postPageOpened(params) {
    return { evid: 202, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postPublished' event (evid: 203)
 *
 * Event description: triggered when post published
 *
 * @param {postPublishedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {number} image_count - image_count in the post
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {boolean} is_edited - 0 - new post / 1 - edit (to know if its posted for first time)
 * @property {string} role - member role (owner, writer, member)
 * @property {string} url - url
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {boolean} has_gallery - NOT IN USE: 0 - no gallery / 1 - published with gallery
 * @property {string} referral_info - NOT IN USE: external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {string} gallery_layout - NOT IN USE: type of the gallery
 * @property {number} giphy_count - count of used  giphy gifs
 * @property {string} tag_ids - array of tags
 * @property {number} categories_count - NOT IN USE: categories_count that post was assigned to
 * @property {boolean} used_html - 0 - didnt used html component / 1 - used html component
 * @property {GUID} post_stable_id - stable id
 * @property {string} language - for multilingual
 * @property {number} character_count - The number of characters on the post
 * @property {string} community_id - NOT IN USE: community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {string} translation_id - the post_stable_id of the translated post
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} author_id - author id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {number} video_count - video_count in the post
 * @property {number} gallery_image_count - NOT IN USE: gallery_image_count
 * @property {boolean} is_published - NOT IN USE (always 1) 0 - not published (draft) / 1 - published
 * @property {string} galleries - will sent as array - [{ layout: 'masonry', count: 5}, { layout: 'whatever', count: 10 }]
 * @property {string} related_post_ids - array of post ids
 * @return {LogObject<postPublishedParams>} LogObject<postPublishedParams>
 */
export function postPublished(params) {
    return { evid: 203, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'commentPublished' event (evid: 204)
 *
 * Event description: triggered when UoU published a comment
 *
 * @param {commentPublishedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {number} image_count - image_count in the post
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {boolean} is_edited - 0 - new post / 1 - edit (to know if its posted for first time)
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {number} giphy_count - count of used  giphy gifs
 * @property {string} comment_id - the id of the comment
 * @property {GUID} post_stable_id - immutable post_id
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} original_instance_id - in the members app events. holds the child app (forum/blog) instance_id
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {number} video_count - video_count in the post
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<commentPublishedParams>} LogObject<commentPublishedParams>
 */
export function commentPublished(params) {
    return { evid: 204, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postDraftCreated' event (evid: 205)
 *
 * Event description: triggered when a post draft is created (first save)
 *
 * @param {postDraftCreatedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {string} referral_info - NOT IN USE: external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_translation - 1- translation, 0  - no translation
 * @property {GUID} post_stable_id - stable id
 * @property {string} language - for multilingual
 * @property {string} community_id - NOT IN USE: community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<postDraftCreatedParams>} LogObject<postDraftCreatedParams>
 */
export function postDraftCreated(params) {
    return { evid: 205, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postDeleted' event (evid: 206)
 *
 * Event description: triggered when post deleted
 *
 * @param {postDeletedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} original_instance_id - in the members app events. holds the child app (forum/blog) instance_id
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<postDeletedParams>} LogObject<postDeletedParams>
 */
export function postDeleted(params) {
    return { evid: 206, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postEditorOpened' event (evid: 207)
 *
 * Event description: triggered when post editor opened
 *
 * @param {postEditorOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {string} referral_info - NOT IN USE: external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} community_id - NOT IN USE: community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<postEditorOpenedParams>} LogObject<postEditorOpenedParams>
 */
export function postEditorOpened(params) {
    return { evid: 207, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'writeNewPostClick' event (evid: 208)
 *
 * Event description: triggered when click on write new post CTA
 *
 * @param {writeNewPostClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {string} page_name - page from where the user clicke
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} original_instance_id - in the members app events. holds the child app (forum/blog) instance_id
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<writeNewPostClickParams>} LogObject<writeNewPostClickParams>
 */
export function writeNewPostClick(params) {
    return { evid: 208, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'likeClick' event (evid: 209)
 *
 * Event description: triggered when uou clicking like or unlike
 *
 * @param {likeClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - comment_id of like clicked (can be used to separate like clicks between comments and posts). Currently OneApp only
 * @property {GUID} post_stable_id - new immutable post_id
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} original_instance_id - in the members app events. holds the child app (forum/blog) instance_id
 * @property {boolean} flag - 0 - unliked / 1 - like
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {number} likes_count - current like count in the site
 * @property {string} member_id - member_id
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<likeClickParams>} LogObject<likeClickParams>
 */
export function likeClick(params) {
    return { evid: 209, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'scrollPositionInPostForReadingTime' event (evid: 2101)
 *
 * Event description: To monitor scroll position inside post page
 *
 * @param {scrollPositionInPostForReadingTimeParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} scrolled_top - how much scrolled on the top of the screen
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {number} bottom_position - position of the bottom of the post in a page
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {number} scrollable_heights - whole page scrollable height (if we are sending everything just while viewing the post, might not be needed)
 * @property {GUID} visitor_id - visitor_id
 * @property {number} browser_width - width of the browser (Just nice to have, not yet know if we will use it; just Medium sends it)
 * @property {GUID} post_stable_id - post_stable_id
 * @property {number} percent_scrolled - percentage of scrolled in a post page (just for analytics and just nice to have)
 * @property {number} timediff - time diff from the start of the session
 * @property {DateTime} view_start - when the view started
 * @property {string} reading_session_id - for each post reading generate id; to be able to join between scroll events and active screen event
 * @property {number} scrolled_bottom - how much scrolled on the bottom of the screen
 * @property {number} top_position - position of the top of the post in a page
 * @return {LogObject<scrollPositionInPostForReadingTimeParams>} LogObject<scrollPositionInPostForReadingTimeParams>
 */
export function scrollPositionInPostForReadingTime(params) {
    return { evid: 2101, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'activeScreenTimeInPostForReadingTime' event (evid: 2102)
 *
 * Event description: To monitor scroll position inside post page
 *
 * @param {activeScreenTimeInPostForReadingTimeParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {boolean} is_on - indicate with TRUE if visitor has turned on the tab and FALSE if switched off tab with a post
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} reading_session_id - for each post reading generate id; to be able to join between scroll events and active screen event
 * @return {LogObject<activeScreenTimeInPostForReadingTimeParams>} LogObject<activeScreenTimeInPostForReadingTimeParams>
 */
export function activeScreenTimeInPostForReadingTime(params) {
    return { evid: 2102, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppHidePost' event (evid: 2103)
 *
 * Event description: Hide/unhide post action in OneApp.
 *
 * @param {oneAppHidePostParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - valid, if the action happened in discovery page
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} reason - selected reason for hiding the post
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} action - hide/unhide (unhide - if presses undo)
 * @return {LogObject<oneAppHidePostParams>} LogObject<oneAppHidePostParams>
 */
export function oneAppHidePost(params) {
    return { evid: 2103, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'blogWidgetLoadStart' event (evid: 2104)
 *
 * Event description: Blog widget load start
 *
 * @param {blogWidgetLoadStartParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} widget_id - widget_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} page_id - page_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} msid - msid
 * @property {Url} page_url - page_url
 * @return {LogObject<blogWidgetLoadStartParams>} LogObject<blogWidgetLoadStartParams>
 */
export function blogWidgetLoadStart(params) {
    return { evid: 2104, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'blogWidgetLoadFinish' event (evid: 2105)
 *
 * Event description: Blog widget load finish
 *
 * @param {blogWidgetLoadFinishParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} duration - duration
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} widget_id - widget_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} page_id - page_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} msid - msid
 * @property {Url} page_url - page_url
 * @return {LogObject<blogWidgetLoadFinishParams>} LogObject<blogWidgetLoadFinishParams>
 */
export function blogWidgetLoadFinish(params) {
    return { evid: 2105, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'newCategoryAddedToBlog' event (evid: 211)
 *
 * Event description: triggered when a new category added in the app settings
 *
 * @param {newCategoryAddedToBlogParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} referral_info - external links - facebook, social networks. if from wix then null
 * @property {GUID} visitor_id - visitor_id
 * @property {number} category_count - current amount of categories i the blog
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} community_id - community_id (instance of members app)
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor, avdanced_post_settings
 * @property {GUID} original_instance_id - in the members app events. holds the child app (forum/blog) instance_id
 * @property {string} category_id - category_id
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<newCategoryAddedToBlogParams>} LogObject<newCategoryAddedToBlogParams>
 */
export function newCategoryAddedToBlog(params) {
    return { evid: 211, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'clickOnTag' event (evid: 212)
 *
 * Event description: triggered whenever a user clicks on a tag
 *
 * @param {clickOnTagParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - role
 * @property {GUID} biToken - msid
 * @property {string} post_id - post id if tag was in the post page
 * @property {string} tagid - tag id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - where did the action take place
 * @property {string} tag_name - tag name
 * @return {LogObject<clickOnTagParams>} LogObject<clickOnTagParams>
 */
export function clickOnTag(params) {
    return { evid: 212, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'sharePostModalOpenClick' event (evid: 213)
 *
 * Event description: triggered when uou clicks on share on three dots to open share modal
 *
 * @param {sharePostModalOpenClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place - feed/post
 * @property {string} member_id - member_id
 * @return {LogObject<sharePostModalOpenClickParams>} LogObject<sharePostModalOpenClickParams>
 */
export function sharePostModalOpenClick(params) {
    return { evid: 213, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'sharePostClick' event (evid: 214)
 *
 * Event description: triggered when uou clicks on share on three dots
 *
 * @param {sharePostClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - new immutable post_id
 * @property {string} origin - which page the action took place - feed/post/modal
 * @property {string} member_id - member_id
 * @property {string} shareType - Which share method was chosen - Facebook/Linkedin/Copy Url, etc
 * @return {LogObject<sharePostClickParams>} LogObject<sharePostClickParams>
 */
export function sharePostClick(params) {
    return { evid: 214, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'blogFeedPostPageSearch' event (evid: 216)
 *
 * Event description: triggered when the user uses the search box on the blog feed and on the blog page.
 *
 * @param {blogFeedPostPageSearchParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} search_term - the text input
 * @property {GUID} post_stable_id - post stable id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @return {LogObject<blogFeedPostPageSearchParams>} LogObject<blogFeedPostPageSearchParams>
 */
export function blogFeedPostPageSearch(params) {
    return { evid: 216, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'fileUploaded' event (evid: 217)
 *
 * Event description: Event triggered when a person tries to upload a file to a Blog post
 *
 * @param {fileUploadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} file_ext - File extension
 * @property {boolean} success - 0 - upload failed / 1 - upload successful
 * @property {string} screen_name - screen_name
 * @property {string} file_id - The file identifier (null if not successful)
 * @property {string} role - role
 * @property {GUID} biToken - metasite id
 * @property {number} file_size - The size of the file being uploaded (in bytes)
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} mime_type - File type
 * @property {string} origin - post (for now just post)
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<fileUploadedParams>} LogObject<fileUploadedParams>
 */
export function fileUploaded(params) {
    return { evid: 217, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'newEvent' event (evid: 218)
 *
 * Event description: When error page is shown in Blog app, i.e.
 * "We Couldn’t Find This Page
 * Check out some of the other great posts in this blog."
 *
 * @param {newEventParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - role in blog
 * @property {GUID} biToken - msid
 * @property {string} pathname - link to post/feed
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} page_type - Feed or Post
 * @return {LogObject<newEventParams>} LogObject<newEventParams>
 */
export function newEvent(params) {
    return { evid: 218, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'fileDownloaded' event (evid: 219)
 *
 * Event description: Event triggered when a person tries to download a file from a blog post
 *
 * @param {fileDownloadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} file_ext - File extension
 * @property {boolean} success - 0 - no download link / 1 - download link received
 * @property {string} screen_name - screen_name
 * @property {string} file_id - The file identifier
 * @property {string} role - role
 * @property {GUID} biToken - msid
 * @property {number} file_size - The size of the file being downloaded (in bytes)
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} mime_type - File type
 * @property {string} origin - post
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<fileDownloadedParams>} LogObject<fileDownloadedParams>
 */
export function fileDownloaded(params) {
    return { evid: 219, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'categoryLabelClick' event (evid: 220)
 *
 * Event description: triggered when uou clicks on a category label
 *
 * @param {categoryLabelClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_name - category name
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {boolean} is_main - is the label clicked main category?
 * @property {string} category_id - category_id
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site member id
 * @return {LogObject<categoryLabelClickParams>} LogObject<categoryLabelClickParams>
 */
export function categoryLabelClick(params) {
    return { evid: 220, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'advancedPostSettingsDoneClick' event (evid: 223)
 *
 * Event description: Clicking Done in advance post settings
 *
 * @param {advancedPostSettingsDoneClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {boolean} is_changed_search_engine_title - Changed search engine title
 * @property {string} screen_name - screen_name
 * @property {boolean} is_excerpt_written - 1 - added text, 0 - didn't
 * @property {string} role - Role of user - writer or admin
 * @property {GUID} biToken - MSID
 * @property {boolean} is_on_feature_post_toggle - 1 - on, 0 - off
 * @property {GUID} visitor_id - visitor_id
 * @property {string} button_name - Save, Save and Publish
 * @property {boolean} is_publish_date_edited - Post publish date edited
 * @property {boolean} is_author_changed - User changed author
 * @property {number} categories_chosen - How many categories was chosen
 * @property {boolean} is_written_description - Written a description
 * @property {GUID} post_stable_id - post_stable_id
 * @property {boolean} is_changed_excerpt - If excerpt was changed
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {boolean} is_changed_url - Changed URL
 * @property {boolean} is_cover_image_changed - Changed cover image
 * @property {boolean} is_published - Is post published (1-published)
 * @property {boolean} is_on_display_cover_toggle - 1 - on, 0 - off
 * @return {LogObject<advancedPostSettingsDoneClickParams>} LogObject<advancedPostSettingsDoneClickParams>
 */
export function advancedPostSettingsDoneClick(params) {
    return { evid: 223, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'advancedPostSettingsTabOpen' event (evid: 224)
 *
 * Event description: Tab opened in advanced post settings (default tab does not count)
 *
 * @param {advancedPostSettingsTabOpenParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - Role of user - writer or admin
 * @property {GUID} biToken - MSID
 * @property {string} tab_name - Which tab was opened in post settings
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<advancedPostSettingsTabOpenParams>} LogObject<advancedPostSettingsTabOpenParams>
 */
export function advancedPostSettingsTabOpen(params) {
    return { evid: 224, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'advancedPostSettingsOpen' event (evid: 228)
 *
 * Event description: Advanced post settings opened
 *
 * @param {advancedPostSettingsOpenParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - Role of user - writer or admin
 * @property {GUID} biToken - MSID
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {boolean} is_published - Is post published (1-published)
 * @return {LogObject<advancedPostSettingsOpenParams>} LogObject<advancedPostSettingsOpenParams>
 */
export function advancedPostSettingsOpen(params) {
    return { evid: 228, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'followUnfollowPost' event (evid: 229)
 *
 * Event description: When a user Follows or unfollows a post.
 * this is event was originally created for the member's area post 3 dot menu
 *
 * @param {followUnfollowPostParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_followed - 1- followed, 0- unfollowed
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<followUnfollowPostParams>} LogObject<followUnfollowPostParams>
 */
export function followUnfollowPost(params) {
    return { evid: 229, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'pinUnpinToFeed' event (evid: 230)
 *
 * Event description: When a user Pins or unpins a post.
 * this is event was originally created for the member's area post 3 dot menu
 *
 * @param {pinUnpinToFeedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} isPinned - 1- Pinned, 0- Unpinned
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<pinUnpinToFeedParams>} LogObject<pinUnpinToFeedParams>
 */
export function pinUnpinToFeed(params) {
    return { evid: 230, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'enableDisableComments' event (evid: 231)
 *
 * Event description: When a user Enables or Disables comments on a post.
 * this is event was originally created for the member's area post 3 dot menu
 *
 * @param {enableDisableCommentsParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_commentable - 1- comments enabled, 0 - comments disabled
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<enableDisableCommentsParams>} LogObject<enableDisableCommentsParams>
 */
export function enableDisableComments(params) {
    return { evid: 231, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'duplicatePost' event (evid: 232)
 *
 * Event description: When a user duplicates a post.
 * this is event was originally created for the member's area drafts 3 dot menu
 *
 * @param {duplicatePostParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<duplicatePostParams>} LogObject<duplicatePostParams>
 */
export function duplicatePost(params) {
    return { evid: 232, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'commentEditClick' event (evid: 233)
 *
 * Event description: When a user clicks to edit a post.
 * this is event was originally created for the member's area comments 3 dot menu
 *
 * @param {commentEditClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - the comment id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<commentEditClickParams>} LogObject<commentEditClickParams>
 */
export function commentEditClick(params) {
    return { evid: 233, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'deleteCommentClick' event (evid: 234)
 *
 * Event description: When a user clicks to delete a post.
 * this is event was originally created for the member's area comments 3 dot menu
 *
 * @param {deleteCommentClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - the comment id
 * @property {GUID} post_stable_id - new immutable post_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<deleteCommentClickParams>} LogObject<deleteCommentClickParams>
 */
export function deleteCommentClick(params) {
    return { evid: 234, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'shareCommentClick' event (evid: 235)
 *
 * Event description: When a user clicks to share a post.
 * this is event was originally created for the member's area comments 3 dot menu
 *
 * @param {shareCommentClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - the comment id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<shareCommentClickParams>} LogObject<shareCommentClickParams>
 */
export function shareCommentClick(params) {
    return { evid: 235, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'clickOnAuthor' event (evid: 236)
 *
 * Event description: When a user clicks on the author's name.
 * this event was originally created for the member's area
 *
 * @param {clickOnAuthorParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @property {string} pageName - the page where the action took place (added for members area)
 * @return {LogObject<clickOnAuthorParams>} LogObject<clickOnAuthorParams>
 */
export function clickOnAuthor(params) {
    return { evid: 236, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'clickOnEditMembersArea' event (evid: 237)
 *
 * Event description: When a user clicks on the edit name.
 * this event was originally created for the member's area
 *
 * @param {clickOnEditMembersAreaParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - role
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} object - comment, post
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @return {LogObject<clickOnEditMembersAreaParams>} LogObject<clickOnEditMembersAreaParams>
 */
export function clickOnEditMembersArea(params) {
    return { evid: 237, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'clickOnTitle' event (evid: 238)
 *
 * Event description: When a user clicks on the edit name.
 * this event was originally created for the member's area
 *
 * @param {clickOnTitleParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids. JSON array string
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} title_type - the type of the title (draft, post, excerpt)
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @return {LogObject<clickOnTitleParams>} LogObject<clickOnTitleParams>
 */
export function clickOnTitle(params) {
    return { evid: 238, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postPage3DotsClick' event (evid: 240)
 *
 * Event description: Triggered when the user clicks on the 3 dots menu, on the post page
 *
 * @param {postPage3DotsClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - role
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} tabName - tab name
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} language - language
 * @property {string} translation_id - translation id
 * @return {LogObject<postPage3DotsClickParams>} LogObject<postPage3DotsClickParams>
 */
export function postPage3DotsClick(params) {
    return { evid: 240, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postsPage3DotsMenuOptionClick' event (evid: 241)
 *
 * Event description: Whenever a user clicks one of the options from the 3 dots menu, in the Post page on the live site
 *
 * @param {postsPage3DotsMenuOptionClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instace
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - role
 * @property {GUID} biToken - msid
 * @property {string} post_id - post id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post stable id
 * @property {string} language - the language ML
 * @property {string} translation_id - translation
 * @property {string} action - share, edit, print etc..
 * @return {LogObject<postsPage3DotsMenuOptionClickParams>} LogObject<postsPage3DotsMenuOptionClickParams>
 */
export function postsPage3DotsMenuOptionClick(params) {
    return { evid: 241, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'postPageBottomIconsClick' event (evid: 242)
 *
 * Event description: when the user clicks the on an icon at the bottom of the post (share options not included)
 *
 * @param {postPageBottomIconsClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance
 * @property {string} click_name - click name
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor
 * @property {GUID} post_stable_id - new immutable post_id
 * @property {string} language - language
 * @property {string} origin - which page the action took place - feed/post/modal
 * @property {string} translation_id - translation
 * @return {LogObject<postPageBottomIconsClickParams>} LogObject<postPageBottomIconsClickParams>
 */
export function postPageBottomIconsClick(params) {
    return { evid: 242, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'migrationPostCopyingStatus' event (evid: 401)
 *
 * Event description: Triggered when the migration completes both successfully or failed.
 *
 * @param {migrationPostCopyingStatusParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} bi_token - msid
 * @property {boolean} migration_succeed - 1 - yes / 0 - no
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} visitor_id - visitor_id
 * @property {number} num_posts_total - the number of total posts to copy
 * @property {number} num_posts_unsucc - the number of posts that weren't copied
 * @property {GUID} post_stable_id - post_stable_id
 * @property {boolean} was_post_too_big - was_post_too_big
 * @property {string} error_message - error message if unsuscessfull
 * @property {string} posts_with_too_large_content
 * @property {GUID} msid - msid
 * @property {number} too_large_post_count - too_large_post_count
 * @return {LogObject<migrationPostCopyingStatusParams>} LogObject<migrationPostCopyingStatusParams>
 */
export function migrationPostCopyingStatus(params) {
    return { evid: 401, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppPostFreeDotsClick' event (evid: 450)
 *
 * Event description: triggered when three dots are clicked in OneApp Post View
 *
 * @param {oneAppPostFreeDotsClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - stable id
 * @property {string} language - language
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {string} translation_id - translation id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<oneAppPostFreeDotsClickParams>} LogObject<oneAppPostFreeDotsClickParams>
 */
export function oneAppPostFreeDotsClick(params) {
    return { evid: 450, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppPostFreeDotsButtonClick' event (evid: 451)
 *
 * Event description: triggered when button, after opened three dots (evid 450), are clicked in OneApp
 *
 * @param {oneAppPostFreeDotsButtonClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} button_name - button name clicked inside three dots: (Un)Follow post, Disable(Enable) Comments, Edit, Share, Delete. On Edit, Share and Delete, flag is not used
 * @property {GUID} post_stable_id - stable id
 * @property {string} language - language
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {boolean} is_offline - if the action happened while offline
 * @property {boolean} flag - 0 - disabled / 1 - enabled
 * @property {string} translation_id - the original post stable_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @property {boolean} is_published - 0 - not published (draft) / 1 - published
 * @return {LogObject<oneAppPostFreeDotsButtonClickParams>} LogObject<oneAppPostFreeDotsButtonClickParams>
 */
export function oneAppPostFreeDotsButtonClick(params) {
    return { evid: 451, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppCategoriesPanelOpened' event (evid: 452)
 *
 * Event description: triggered when categories drop down is opened in OneApp
 *
 * @param {oneAppCategoriesPanelOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<oneAppCategoriesPanelOpenedParams>} LogObject<oneAppCategoriesPanelOpenedParams>
 */
export function oneAppCategoriesPanelOpened(params) {
    return { evid: 452, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppChoseCategory' event (evid: 453)
 *
 * Event description: triggered when category filter is chosen in oneapp
 *
 * @param {oneAppChoseCategoryParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in
 * @property {string} category_id - category_id
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<oneAppChoseCategoryParams>} LogObject<oneAppChoseCategoryParams>
 */
export function oneAppChoseCategory(params) {
    return { evid: 453, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppSeeAllClickInPostsFeed' event (evid: 454)
 *
 * Event description: triggered when see all is clicked in posts feed in OneApp
 *
 * @param {oneAppSeeAllClickInPostsFeedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in
 * @property {GUID} site_member_id - site_member_id
 * @property {string} member_id - member_id
 * @return {LogObject<oneAppSeeAllClickInPostsFeedParams>} LogObject<oneAppSeeAllClickInPostsFeedParams>
 */
export function oneAppSeeAllClickInPostsFeed(params) {
    return { evid: 454, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppMigrationModalOpen' event (evid: 455)
 *
 * Event description: triggered when user gets Blog migration modal in OneApp
 *
 * @param {oneAppMigrationModalOpenParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} biToken - msid
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<oneAppMigrationModalOpenParams>} LogObject<oneAppMigrationModalOpenParams>
 */
export function oneAppMigrationModalOpen(params) {
    return { evid: 455, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppMigrationGetInviteClick' event (evid: 456)
 *
 * Event description: triggered when user clicks 'get invited' to new Wix blog
 *
 * @param {oneAppMigrationGetInviteClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} biToken - msid
 * @property {GUID} uuid - uuid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<oneAppMigrationGetInviteClickParams>} LogObject<oneAppMigrationGetInviteClickParams>
 */
export function oneAppMigrationGetInviteClick(params) {
    return { evid: 456, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppPostPageBottomBarClick' event (evid: 457)
 *
 * Event description: When the user clicks on the Bottom bar from within the post page (Like,
 *
 * @param {oneAppPostPageBottomBarClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {string} click_name - Like/Unkike, Comment, Share
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} category_ids - category_ids array
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post stable Id
 * @property {string} origin - which page the action took place in - home_page, category_page, post_page, members_page, recent_posts, mega_menu, post_editor
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - the member Id on the site
 * @return {LogObject<oneAppPostPageBottomBarClickParams>} LogObject<oneAppPostPageBottomBarClickParams>
 */
export function oneAppPostPageBottomBarClick(params) {
    return { evid: 457, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppBookmarkClick' event (evid: 458)
 *
 * Event description: triggered when uou clicking or unclicking bookmark
 *
 * @param {oneAppBookmarkClickParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {string} role - member role (owner, writer, member)
 * @property {GUID} biToken - msid
 * @property {string} post_id - post_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} click_origin - weather it was on the bottom bar or toast
 * @property {GUID} post_stable_id - new immutable post_id
 * @property {string} origin - which page the action took place in - post_page, blog_feed, my_saved_posts_page
 * @property {boolean} flag - 0 - unliked / 1 - like
 * @property {GUID} app_id - app_id (forum, blog, members)
 * @property {GUID} site_member_id - site_member_id
 * @return {LogObject<oneAppBookmarkClickParams>} LogObject<oneAppBookmarkClickParams>
 */
export function oneAppBookmarkClick(params) {
    return { evid: 458, src: 69, endpoint: 'blog-app-uou', params: params };
}
/**
 * A function to create a log object for the 'oneAppMySavedPostsPageOpened' event (evid: 459)
 *
 * Event description: triggered when blog My saved posts page opened (uou)
 *
 * @param {oneAppMySavedPostsPageOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - holds the child app (forum/blog) instance_id. or the members instance_id
 * @property {boolean} is_demo - is_demo
 * @property {string} screen_name - screen_name
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} post_stable_id - post_stable_id
 * @property {string} origin - which page the action took place in - post_feed, post_page
 * @return {LogObject<oneAppMySavedPostsPageOpenedParams>} LogObject<oneAppMySavedPostsPageOpenedParams>
 */
export function oneAppMySavedPostsPageOpened(params) {
    return { evid: 459, src: 69, endpoint: 'blog-app-uou', params: params };
}
//# sourceMappingURL=index.js.map